import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

// Colores de OviCatalogos
const colors = {
    // Primarios
    primary: '#4169E1',         // Royal Blue
    primaryDark: '#0F172A',     // Azul oscuro
    
    // Accent
    accent: '#10B981',          // Verde
    purple: '#8B5CF6',          // Púrpura
    cyan: '#06B6D4',            // Cian
    
    // Textos
    textPrimary: '#0F172A',
    textSecondary: '#475569',
    textMuted: '#94A3B8',
    
    // Semánticos
    warning: '#FFA726',
    
    // Neutrales
    white: '#FFFFFF',
    light: '#F8FAFC',
    gray: '#CBD5E1',
    border: '#E2E8F0',
    dark: '#475569',
};

const styles = StyleSheet.create({
    page: {
        padding: 40,
        fontSize: 10,
        fontFamily: 'Helvetica',
        backgroundColor: colors.white,
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 30,
        paddingBottom: 20,
        borderBottom: `3 solid ${colors.primary}`,
    },
    logoSection: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    logo: {
        width: 50,
        height: 50,
        marginRight: 15,
        borderRadius: 25, // Hace el logo circular (50% del tamaño)
    },
    companyInfo: {
        flexDirection: 'column',
    },
    companyName: {
        fontSize: 24,
        fontWeight: 'bold',
        color: colors.primary,
        marginBottom: 4,
    },
    companySubtitle: {
        fontSize: 9,
        color: colors.dark,
        marginBottom: 6,
    },
    companyDetails: {
        fontSize: 8,
        color: colors.textMuted,
        marginBottom: 2,
    },
    documentSection: {
        alignItems: 'flex-end',
    },
    documentTitle: {
        fontSize: 20,
        fontWeight: 'bold',
        color: colors.primary,
        marginBottom: 8,
        letterSpacing: 1,
    },
    documentNumber: {
        fontSize: 12,
        color: colors.primaryDark,
        fontWeight: 'bold',
        marginBottom: 4,
        backgroundColor: colors.light,
        padding: '6 12',
        borderRadius: 4,
        border: `1 solid ${colors.primary}`,
    },
    documentDate: {
        fontSize: 9,
        color: colors.dark,
        marginTop: 4,
    },
    infoContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 25,
        gap: 20,
    },
    infoBox: {
        width: '48%',
        backgroundColor: colors.light,
        padding: 15,
        borderRadius: 8,
        borderLeft: `4 solid ${colors.primary}`,
    },
    infoBoxAlt: {
        width: '48%',
        backgroundColor: '#ecfdf5',
        padding: 15,
        borderRadius: 8,
        borderLeft: `4 solid #10B981`,
    },
    infoTitle: {
        fontSize: 11,
        fontWeight: 'bold',
        color: colors.primaryDark,
        marginBottom: 10,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    infoRow: {
        flexDirection: 'row',
        marginBottom: 6,
    },
    infoLabel: {
        fontSize: 9,
        color: colors.dark,
        width: '40%',
        fontWeight: 'bold',
    },
    infoValue: {
        fontSize: 9,
        color: colors.primaryDark,
        width: '60%',
    },
    totalsContainer: {
        marginTop: 25,
        paddingTop: 20,
        borderTop: `2 solid ${colors.gray}`,
    },
    totalsSection: {
        marginLeft: 'auto',
        width: '50%',
        backgroundColor: '#dbeafe',
        padding: 20,
        borderRadius: 8,
        border: `2 solid #3B82F6`,
        minHeight: 120,
        justifyContent: 'center',
        alignItems: 'center',
    },
    totalRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingVertical: 7,
        borderBottom: `1 solid ${colors.gray}`,
    },
    totalLabel: {
        fontSize: 10,
        color: colors.dark,
    },
    totalValue: {
        fontSize: 10,
        color: colors.primaryDark,
        fontWeight: 'bold',
    },
    grandTotalRow: {
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
    },
    grandTotalLabel: {
        fontSize: 11,
        fontWeight: 'bold',
        color: '#1e40af',
        marginBottom: 12,
        textTransform: 'uppercase',
        letterSpacing: 1,
    },
    grandTotalValue: {
        fontSize: 28,
        fontWeight: 'bold',
        color: '#1e40af',
    },
    notesSection: {
        marginTop: 25,
        padding: 15,
        backgroundColor: '#FFF8E1',
        borderRadius: 8,
        borderLeft: `4 solid ${colors.warning}`,
        minHeight: 120,
    },
    notesTitle: {
        fontSize: 10,
        fontWeight: 'bold',
        color: '#E65100',
        marginBottom: 8,
    },
    notesText: {
        fontSize: 9,
        color: '#BF360C',
        lineHeight: 1.5,
    },
    footer: {
        position: 'absolute',
        bottom: 30,
        left: 40,
        right: 40,
        paddingTop: 15,
        borderTop: `1 solid ${colors.gray}`,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    footerText: {
        fontSize: 8,
        color: colors.textMuted,
    },
    footerBold: {
        fontSize: 8,
        color: colors.dark,
        fontWeight: 'bold',
    },
    accentBadge: {
        backgroundColor: colors.accent,
        padding: '4 8',
        borderRadius: 4,
    },
});

const FlyMarketInvoicePDF = ({ invoice }) => {
    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('es-CO', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                {/* Header */}
                <View style={styles.header}>
                    <View style={styles.logoSection}>
                        <Image
                            src="/logos/ovicatalogos-logo512.png"
                            style={styles.logo}
                        />
                        <View style={styles.companyInfo}>
                            <Text style={styles.companyName}>OVICATALOGOS</Text>
                            <Text style={styles.companySubtitle}>Minymol S.A.S.</Text>
                            <Text style={styles.companyDetails}>NIT: 901964524-2</Text>
                        </View>
                    </View>
                    <View style={styles.documentSection}>
                        <Text style={styles.documentTitle}>RECIBO DE CAJA</Text>
                        <Text style={styles.documentNumber}>Nro. {invoice.invoiceNumber}</Text>
                        <Text style={styles.documentDate}>Fecha de emisión: {formatDate(invoice.issueDate)}</Text>
                        <View style={{ marginTop: 10, padding: 8, backgroundColor: '#d1fae5', borderRadius: 4, border: '2 solid #10B981' }}>
                            <Text style={{ fontSize: 10, fontWeight: 'bold', color: '#065f46', textAlign: 'center' }}>✓ PAGADO</Text>
                        </View>
                    </View>
                </View>

                {/* Client and Payment Info */}
                <View style={styles.infoContainer}>
                    <View style={styles.infoBox}>
                        <Text style={styles.infoTitle}>Recibido de</Text>
                        <View style={styles.infoRow}>
                            <Text style={styles.infoLabel}>Razón Social:</Text>
                            <Text style={styles.infoValue}>{invoice.companyName}</Text>
                        </View>
                        <View style={styles.infoRow}>
                            <Text style={styles.infoLabel}>Nombre:</Text>
                            <Text style={styles.infoValue}>{invoice.clientName}</Text>
                        </View>
                        <View style={styles.infoRow}>
                            <Text style={styles.infoLabel}>Teléfono:</Text>
                            <Text style={styles.infoValue}>{invoice.clientPhone}</Text>
                        </View>
                        <View style={styles.infoRow}>
                            <Text style={styles.infoLabel}>Ciudad:</Text>
                            <Text style={styles.infoValue}>{invoice.clientCity}, {invoice.clientDepartment}</Text>
                        </View>
                    </View>
                    <View style={styles.infoBoxAlt}>
                        <Text style={styles.infoTitle}>Detalles del Pago</Text>
                        <View style={styles.infoRow}>
                            <Text style={styles.infoLabel}>Forma de Pago:</Text>
                            <Text style={styles.infoValue}>Transferencia Bancaria</Text>
                        </View>
                        <View style={styles.infoRow}>
                            <Text style={styles.infoLabel}>Tipo de Plan:</Text>
                            <Text style={styles.infoValue}>{invoice.planType}</Text>
                        </View>
                        <View style={styles.infoRow}>
                            <Text style={styles.infoLabel}>Fecha de Pago:</Text>
                            <Text style={styles.infoValue}>{formatDate(invoice.issueDate)}</Text>
                        </View>
                    </View>
                </View>

                {/* Concept */}
                <View style={{ marginTop: 20, marginBottom: 20, padding: 15, backgroundColor: colors.light, borderRadius: 8, border: `1 solid ${colors.border}` }}>
                    <Text style={{ fontSize: 10, fontWeight: 'bold', color: colors.primaryDark, marginBottom: 8, textTransform: 'uppercase' }}>
                        CONCEPTO DEL PAGO
                    </Text>
                    <Text style={{ fontSize: 9, color: colors.dark, lineHeight: 1.5 }}>
                        {invoice.concept}
                    </Text>
                </View>

                {/* Amount */}
                <View style={styles.totalsContainer}>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: 20 }}>
                        <View style={{ width: '48%' }}>
                            <View style={styles.notesSection}>
                                <Text style={styles.notesTitle}>OviedoTech S.A.S</Text>
                                <Text style={styles.notesText}>Desarrolladores de OviCatalogos, FlyMarket, FlyStock y Minymol</Text>
                                <View style={{ marginTop: 10, paddingTop: 10, borderTop: '1 solid #f59e0b' }}>
                                    <Text style={{ fontSize: 9, fontWeight: 'bold', color: '#92400e', marginBottom: 8 }}>Este documento certifica el pago recibido</Text>
                                    <Text style={{ fontSize: 8, color: '#78350f', marginBottom: 2 }}>Gracias por su pago</Text>
                                    <Text style={{ fontSize: 8, color: '#78350f' }}>Para consultas: notificaciones@minymol.com</Text>
                                </View>
                            </View>
                        </View>
                        <View style={styles.totalsSection}>
                            <View style={styles.grandTotalRow}>
                                <Text style={styles.grandTotalLabel}>MONTO RECIBIDO</Text>
                                <Text style={styles.grandTotalValue}>{formatCurrency(invoice.total)}</Text>
                            </View>
                        </View>
                    </View>
                </View>

                {/* Footer */}
                <View style={styles.footer}>
                    <Text style={styles.footerText}>
                        Generado por <Text style={styles.footerBold}>OviCatalogos System</Text>
                    </Text>
                    <Text style={styles.footerText}>
                        {formatDate(new Date())}
                    </Text>
                </View>
            </Page>
        </Document>
    );
};

export default FlyMarketInvoicePDF;
